import type { ModeloId, MovelTipo, Pt, Tabuleiro, Vaga } from './engine'

/**
 * Planta livre do Escritório (SPEC-20261011-2023): quem administra desenha o próprio andar numa
 * grade de w x h unidades (1 unidade = um quadradinho do tabuleiro). Chão por quadradinho, paredes
 * nas linhas da grade e áreas fechadas por paredes que viram sala de chamada.
 */

export type Material = 'madeira' | 'madeira_escura' | 'carpete' | 'porcelanato' | 'concreto' | 'marmore' | 'grama'
export type TipoDeParede = 'parede' | 'meia' | 'vidro' | 'janela' | 'porta'
/** [x, y, 0 = deitada (de x a x+1 na linha y) | 1 = em pé (de y a y+1 na coluna x), tipo, cor] */
export type Parede = [number, number, 0 | 1, TipoDeParede, string | null]
export type SalaDaPlanta = { lot: number; nome: string; modelo: ModeloId; celulas: number[] }
export type Planta = {
  v: 1
  w: number
  h: number
  /** Chão de cada quadradinho, linha a linha: "madeira", "carpete:#4a5fb8"... ou null (sem chão). */
  chao: (string | null)[]
  paredes: Parede[]
  salas: SalaDaPlanta[]
}

export const MATERIAIS: { id: Material; nome: string; tinge: boolean; cor: string; amostra: string }[] = [
  { id: 'madeira', nome: 'Madeira clara', tinge: false, cor: '', amostra: '#d3a46c' },
  { id: 'madeira_escura', nome: 'Madeira escura', tinge: false, cor: '', amostra: '#7a5134' },
  { id: 'carpete', nome: 'Carpete', tinge: true, cor: '#4a5fb8', amostra: '#4a5fb8' },
  { id: 'porcelanato', nome: 'Porcelanato', tinge: true, cor: '#e9e7e2', amostra: '#e9e7e2' },
  { id: 'concreto', nome: 'Cimento queimado', tinge: false, cor: '', amostra: '#c3c7cf' },
  { id: 'marmore', nome: 'Mármore', tinge: false, cor: '', amostra: '#efece6' },
  { id: 'grama', nome: 'Grama sintética', tinge: false, cor: '', amostra: '#5aa845' },
]
export const TIPOS_DE_PAREDE: { id: TipoDeParede; nome: string; dica: string }[] = [
  { id: 'parede', nome: 'Parede', dica: 'Parede inteira, pintável' },
  { id: 'meia', nome: 'Meia parede', dica: 'Mureta com vidro em cima' },
  { id: 'vidro', nome: 'Vidro', dica: 'Divisória de vidro do chão ao teto' },
  { id: 'janela', nome: 'Janela', dica: 'Parede com janela' },
  { id: 'porta', nome: 'Porta', dica: 'Passagem com batente' },
]
export const CORES_DE_PAREDE = ['#f1ede6', '#dfe4ec', '#e6f4ee', '#fbe6da', '#f1eaf9', '#ece3cf', '#3a3f55', '#2f6653', '#8a3a48', '#4a5fb8']
export const CORES_DE_CHAO = ['#4a5fb8', '#5b6478', '#2f6653', '#8a3a48', '#d9a27f', '#b89bd8', '#a7dcc8', '#3a3f55', '#e9e7e2', '#f2c14e']

export const materialDe = (codigo: string | null) => (codigo ? (codigo.split(':')[0] as Material) : null)
export const corDe = (codigo: string | null) => (codigo?.includes(':') ? (codigo.split(':')[1] ?? null) : null)
export const codigoDoChao = (m: Material, cor: string | null) => (MATERIAIS.find((x) => x.id === m)?.tinge && cor ? `${m}:${cor}` : m)

export function plantaVazia(w = 30, h = 18, chao: string | null = 'madeira'): Planta {
  return { v: 1, w, h, chao: Array.from({ length: w * h }, () => chao), paredes: [], salas: [] }
}

export const chaveDaParede = (x: number, y: number, o: 0 | 1) => `${x},${y},${o}`

/** Paredes por linha da grade, para achar rápido. */
export function mapaDeParedes(p: Planta) {
  return new Map(p.paredes.map((pa) => [chaveDaParede(pa[0], pa[1], pa[2]), pa] as const))
}

/** A sala que tem esse quadradinho, se tiver. */
export const salaDaCelula = (p: Planta, i: number) => p.salas.find((s) => s.celulas.includes(i)) ?? null

/**
 * A área fechada em volta do quadradinho i: anda pelos quadradinhos com chão sem atravessar
 * parede nenhuma (porta e vidro também fecham). Se escapar pela borda da planta sem parede, ou
 * ficar grande demais, não é uma sala.
 */
export function areaFechada(p: Planta, i: number): { celulas: number[] } | { erro: string } {
  if (!p.chao[i]) return { erro: 'Aqui não tem chão.' }
  const paredes = mapaDeParedes(p)
  const visto = new Set<number>([i])
  const fila = [i]
  const limite = Math.min(240, Math.floor(p.w * p.h * 0.6))
  while (fila.length) {
    const c = fila.pop() as number
    const x = c % p.w
    const y = Math.floor(c / p.w)
    const passos: [number, number, string][] = [
      [x, y - 1, chaveDaParede(x, y, 0)],
      [x, y + 1, chaveDaParede(x, y + 1, 0)],
      [x - 1, y, chaveDaParede(x, y, 1)],
      [x + 1, y, chaveDaParede(x + 1, y, 1)],
    ]
    for (const [nx, ny, k] of passos) {
      if (paredes.has(k)) continue
      if (nx < 0 || ny < 0 || nx >= p.w || ny >= p.h) return { erro: 'Essa área não está fechada. Feche com paredes (porta e vidro contam).' }
      const n = ny * p.w + nx
      if (visto.has(n)) continue
      if (!p.chao[n]) return { erro: 'Essa área não está fechada. Feche com paredes (porta e vidro contam).' }
      visto.add(n)
      fila.push(n)
      if (visto.size > limite) return { erro: 'Essa área é grande demais para uma sala. Divida com paredes.' }
    }
  }
  return { celulas: [...visto].sort((a, b) => a - b) }
}

/** Salas que perderam a parede em volta (ou o chão) deixam de ser sala. */
export function salasQueContinuam(p: Planta): SalaDaPlanta[] {
  return p.salas.flatMap((s) => {
    const r = areaFechada(p, s.celulas[0] as number)
    if ('erro' in r) return []
    return [{ ...s, celulas: r.celulas }]
  })
}

export const proximaVaga = (p: Planta) => Math.max(99, ...p.salas.map((s) => s.lot)) + 1

const cache = new WeakMap<Planta, Tabuleiro>()

/** O tabuleiro da planta livre, no formato dos prontos (o desenho vem do kit do Blender). */
export function tabuleiroLivre(p: Planta): Tabuleiro {
  const ja = cache.get(p)
  if (ja) return ja
  const vagas: Vaga[] = p.salas.map((s) => {
    const xs = s.celulas.map((c) => c % p.w)
    const ys = s.celulas.map((c) => Math.floor(c / p.w))
    return { id: s.lot, x0: Math.min(...xs), x1: Math.max(...xs) + 1, y0: Math.min(...ys), y1: Math.max(...ys) + 1, fileira: 'cima', celulas: new Set(s.celulas), w: p.w }
  })
  const predio = { x0: 0, x1: p.w, y0: 0, y1: p.h }
  // a placa da área central vai no meio do maior pedaço de chão fora das salas
  const fora = p.chao.flatMap((c, i) => (c && !p.salas.some((s) => s.celulas.includes(i)) ? [i] : []))
  const mx = fora.length ? fora.reduce((a, i) => a + (i % p.w) + 0.5, 0) / fora.length : p.w / 2
  const my = fora.length ? fora.reduce((a, i) => a + Math.floor(i / p.w) + 0.5, 0) / fora.length : p.h / 2
  const t: Tabuleiro = {
    id: 'livre',
    nome: 'Planta livre',
    descricao: 'Desenhada por quem administra',
    predio,
    vagas,
    centrais: [predio],
    pisos: [],
    entrada: null,
    placaCentral: [mx, my, 20],
    montar: () => [] as Pt[],
    realista: { casca: 'livre' },
    blocos: { casca: 'livre' },
  }
  cache.set(p, t)
  return t
}

/* --------------------------------------------- começar de um modelo --- */

type MovelDoModelo = { kind: MovelTipo; x: number; y: number; variant?: 'n' | 's'; color?: string | null }
export type ModeloDePlanta = { id: string; nome: string; descricao: string; gerar: () => { planta: Planta; moveis: MovelDoModelo[] } }

function desenhista(p: Planta) {
  const pa = mapaDeParedes(p)
  const por = (x: number, y: number, o: 0 | 1, t: TipoDeParede, cor: string | null = null) => pa.set(chaveDaParede(x, y, o), [x, y, o, t, cor])
  return {
    /** Parede deitada de x0 a x1 na linha y. */
    h(x0: number, x1: number, y: number, t: TipoDeParede = 'parede', cor: string | null = null) {
      for (let x = x0; x < x1; x++) por(x, y, 0, t, cor)
    },
    /** Parede em pé de y0 a y1 na coluna x. */
    v(x: number, y0: number, y1: number, t: TipoDeParede = 'parede', cor: string | null = null) {
      for (let y = y0; y < y1; y++) por(x, y, 1, t, cor)
    },
    um(x: number, y: number, o: 0 | 1, t: TipoDeParede, cor: string | null = null) {
      por(x, y, o, t, cor)
    },
    chao(x0: number, y0: number, x1: number, y1: number, codigo: string) {
      for (let y = y0; y < y1; y++) for (let x = x0; x < x1; x++) p.chao[y * p.w + x] = codigo
    },
    sala(x0: number, y0: number, x1: number, y1: number, nome: string, modelo: ModeloId) {
      const celulas: number[] = []
      for (let y = y0; y < y1; y++) for (let x = x0; x < x1; x++) celulas.push(y * p.w + x)
      p.salas.push({ lot: proximaVaga(p), nome, modelo, celulas })
    },
    fechar() {
      p.paredes = [...pa.values()]
    },
  }
}

/** Contorno do prédio: janelas no fundo, paredes dos lados e fachada de vidro com a porta de entrada. */
function contorno(d: ReturnType<typeof desenhista>, w: number, h: number, porta: number) {
  for (let x = 0; x < w; x++) d.um(x, 0, 0, x % 3 === 1 ? 'janela' : 'parede')
  d.v(0, 0, h)
  d.v(w, 0, h)
  d.h(0, w, h, 'vidro')
  d.um(porta, h, 0, 'porta')
}

/** Uma sala com frente de vidro e porta: fundo é o contorno do prédio, lados e frente são novos. */
function salaDeVidro(d: ReturnType<typeof desenhista>, x0: number, y0: number, x1: number, y1: number, frente: 'baixo' | 'cima', nome: string, modelo: ModeloId, carpete: string) {
  d.chao(x0, y0, x1, y1, `carpete:${carpete}`)
  d.v(x0, y0, y1)
  d.v(x1, y0, y1)
  const yf = frente === 'baixo' ? y1 : y0
  d.h(x0, x1, yf, 'vidro')
  d.um(Math.floor((x0 + x1) / 2), yf, 0, 'porta')
  d.sala(x0, y0, x1, y1, nome, modelo)
}

export const MODELOS_DE_PLANTA: ModeloDePlanta[] = [
  {
    id: 'branco',
    nome: 'Em branco',
    descricao: 'Só o chão e as paredes de fora. Você desenha o resto.',
    gerar() {
      const p = plantaVazia(28, 16)
      const d = desenhista(p)
      contorno(d, 28, 16, 13)
      d.fechar()
      return { planta: p, moveis: [] }
    },
  },
  {
    id: 'pequeno',
    nome: 'Escritório pequeno',
    descricao: '3 salas, área aberta com mesas e um cantinho de descanso.',
    gerar() {
      const w = 26
      const h = 15
      const p = plantaVazia(w, h)
      const d = desenhista(p)
      contorno(d, w, h, 12)
      salaDeVidro(d, 0, 0, 6, 5, 'baixo', 'Sala de reunião', 'reuniao', '#4a5fb8')
      salaDeVidro(d, 6, 0, 11, 5, 'baixo', 'Sala 1:1', 'cabine', '#b89bd8')
      salaDeVidro(d, 20, 0, 26, 5, 'baixo', 'Diretoria', 'chefia', '#2f6653')
      d.chao(11, 0, 20, 5, 'porcelanato:#e9e7e2')
      d.fechar()
      return {
        planta: p,
        moveis: [
          { kind: 'mesa', x: 1.3, y: 1.6 },
          { kind: 'sofa', x: 20.8, y: 1.2 },
          { kind: 'estacao_dupla', x: 2, y: 8 },
          { kind: 'estacao_dupla', x: 6, y: 8 },
          { kind: 'estacao_dupla', x: 2, y: 11 },
          { kind: 'estacao_dupla', x: 6, y: 11 },
          { kind: 'tapete', x: 15, y: 9 },
          { kind: 'sofa', x: 15, y: 8.2 },
          { kind: 'poltrona', x: 18.4, y: 9.4 },
          { kind: 'mesinha', x: 15.6, y: 10.2 },
          { kind: 'cafe', x: 13, y: 1.5 },
          { kind: 'bebedouro', x: 18.8, y: 1.6 },
          { kind: 'planta', x: 0.4, y: 13.4 },
          { kind: 'planta', x: 24.6, y: 13.4 },
        ],
      }
    },
  },
  {
    id: 'sede',
    nome: 'Sede com corredor',
    descricao: '4 salas em cima, 3 embaixo e um corredor largo com lounge.',
    gerar() {
      const w = 34
      const h = 20
      const p = plantaVazia(w, h)
      const d = desenhista(p)
      contorno(d, w, h, 16)
      const cores = ['#4a5fb8', '#5b6478', '#d9a27f', '#2f6653']
      const cima: [string, ModeloId][] = [['Sala de reunião', 'reuniao'], ['Sala do time', 'time'], ['Estúdio', 'estudio'], ['Diretoria', 'chefia']]
      cima.forEach(([n, m], i) => salaDeVidro(d, i * 6, 0, i * 6 + 6, 6, 'baixo', n, m, cores[i] as string))
      const baixo: [string, ModeloId][] = [['Copa', 'copa'], ['Sala 1:1', 'cabine'], ['Treinamento', 'reuniao']]
      baixo.forEach(([n, m], i) => {
        const x0 = i === 2 ? 28 : i * 6
        salaDeVidro(d, x0, 14, x0 + 6, 20, 'cima', n, m, i === 0 ? '#a7dcc8' : i === 1 ? '#b89bd8' : '#4a5fb8')
      })
      d.chao(0, 14, 6, 20, 'porcelanato:#e9e7e2')
      d.chao(24, 0, 34, 6, 'madeira')
      d.chao(14, 14, 22, 20, 'marmore')
      d.fechar()
      return {
        planta: p,
        moveis: [
          { kind: 'tapete', x: 9, y: 8.4 },
          { kind: 'sofa', x: 9, y: 7.6 },
          { kind: 'sofa', x: 9, y: 11, variant: 's' },
          { kind: 'mesinha', x: 9.6, y: 9.6 },
          { kind: 'pingue', x: 19, y: 8.6 },
          { kind: 'comunitaria', x: 24.6, y: 8.4 },
          { kind: 'cafe', x: 25.2, y: 1.2 },
          { kind: 'sofa', x: 29.8, y: 4.4 },
          { kind: 'bebedouro', x: 33, y: 7.2 },
          { kind: 'planta', x: 0.4, y: 7 },
          { kind: 'planta', x: 0.4, y: 12.4 },
          { kind: 'luminaria', x: 14.4, y: 8.6 },
          { kind: 'sofa', x: 15.6, y: 15.4 },
          { kind: 'planta', x: 21, y: 15 },
        ],
      }
    },
  },
]
